import { indexBy } from 'remeda';

import { Assignment } from '@finley-technologies/finley-types';

import type {
  BorrowingBaseMechanicsForLoanProduct,
  CreditFacilityAllocation,
  LoanProductConfig,
  TotalAssignableExposureHeuristicConfig,
} from '../../types';

export const createLoanProductOptions = ({
  validLoanProductsForCreditFacilitiesMap,
  borrowingBaseMechanicsMap,
  creditFacilityId,
}: {
  validLoanProductsForCreditFacilitiesMap: Map<string, string[]>;
  borrowingBaseMechanicsMap: Map<string, BorrowingBaseMechanicsForLoanProduct>;
  creditFacilityId?: string;
}) => {
  const loanProductOptions: { value: string; label: string }[] = [];
  if (creditFacilityId) {
    const validLoanProducts =
      validLoanProductsForCreditFacilitiesMap.get(creditFacilityId) ?? [];
    validLoanProducts.forEach((loanProductId) => {
      const loanProduct = borrowingBaseMechanicsMap.get(
        `${creditFacilityId}+${loanProductId}`,
      );
      if (loanProduct) {
        loanProductOptions.push({
          value: loanProductId,
          label: loanProduct.loanProductName,
        });
      }
    });
  }
  return loanProductOptions;
};

export const initTotalAssignableExposureDecisionHeuristic = (
  validLoanProductsForCreditFacilitiesMap: Map<string, string[]>,
  initialDecisionHeuristicConfig?: TotalAssignableExposureHeuristicConfig,
): Assignment.AssignByTotalAssignableExposureConfig => {
  // reversing validLoanProductsForCreditFacilitiesMap
  const loanProductToCreditFacilityMap = new Map<string, string[]>();
  validLoanProductsForCreditFacilitiesMap.forEach(
    (loanProductIds, creditFacilityId) => {
      loanProductIds.forEach((loanProductId) => {
        const validCreditFacilityIds =
          loanProductToCreditFacilityMap.get(loanProductId) ?? [];
        validCreditFacilityIds.push(creditFacilityId);
        loanProductToCreditFacilityMap.set(
          loanProductId,
          validCreditFacilityIds,
        );
      });
    },
  );
  const loanProductConfigs: LoanProductConfig[] = [];

  const loanProductConfigsMap = indexBy(
    initialDecisionHeuristicConfig?.loanProductConfigs || loanProductConfigs,
    ({ loanProductId }) => loanProductId,
  );

  loanProductToCreditFacilityMap.forEach((creditFacilityIds, loanProductId) => {
    if (loanProductId in loanProductConfigsMap) {
      const loanProductConfig = loanProductConfigsMap[loanProductId];
      const creditFacilityAllocationsMap = indexBy(
        loanProductConfig.creditFacilityAllocations,
        ({ creditFacilityId }) => creditFacilityId,
      );
      let balanceSheetAllocation = 1000; // using 1000 to avoid float precision issues
      const creditFacilityAllocations: CreditFacilityAllocation[] = [];
      creditFacilityIds.forEach((creditFacilityId) => {
        if (creditFacilityId in creditFacilityAllocationsMap) {
          const creditFacilityAllocation =
            creditFacilityAllocationsMap[creditFacilityId];
          balanceSheetAllocation -= creditFacilityAllocation.allocation * 1000; // multiplying by 1000 to avoid float precision issues
          creditFacilityAllocations.push(creditFacilityAllocation);
        } else {
          creditFacilityAllocations.push({
            creditFacilityId,
            allocation: 0,
            hardRequirement: 'none',
          });
        }
      });
      loanProductConfigs.push({
        loanProductId,
        balanceSheetAllocation: balanceSheetAllocation / 1000, // dividing by 1000 to avoid float precision issues
        creditFacilityAllocations,
      });
    } else {
      const loanProductConfig: LoanProductConfig = {
        loanProductId,
        balanceSheetAllocation: 1,
        creditFacilityAllocations: creditFacilityIds.map((creditFacilityId) => {
          return {
            creditFacilityId,
            allocation: 0,
            hardRequirement: 'none',
          };
        }),
      };
      loanProductConfigs.push(loanProductConfig);
    }
  });

  return {
    heuristicType: 'assignByTotalAssignableExposure',
    config: { loanProductConfigs },
  };
};

export const initWaterfallByExcessMargin = (
  waterfallConfig?: Assignment.WaterfallByExcessMargin,
): Assignment.WaterfallByExcessMargin => {
  if (waterfallConfig) {
    return waterfallConfig;
  }

  return {
    heuristicType: 'waterfallByExcessMargin',
    config: {
      waterfall: [],
    },
  };
};
